import React from 'react';
import { useHistory } from 'react-router-dom';
import SensorDataGraph from './SensorDataGraph';
import SensorDataTable from './SensorDataTable';
import styles from './SensorDataWidget.module.css';
import { getPublicDashboardToken } from '../../services/dashboardApi';
import HasRolePrivileges from '../../../highorder/HasRolePrivileges';
import { RaaRoles } from '../../../../utility/SecurityRules';

const HIGH_COLOR = '#dc3545';
const LOW_COLOR = '#0d6efd';

// Build 24 hours of gently wobbling stub samples starting from `base`.
const buildStubSamples = (base, amplitude) => {
  const now = Date.now();
  const points = 48; // one sample every 30 minutes
  const hourMs = 60 * 60 * 1000;
  const x = [];
  const y = [];
  for (let i = points - 1; i >= 0; i -= 1) {
    const t = new Date(now - (i * hourMs) / 2);
    // Deterministic pseudo-random wobble so the chart is stable across renders.
    const wobble = Math.sin((points - i) * 0.6) * amplitude;
    x.push(t);
    y.push(Number((base + wobble).toFixed(2)));
  }
  return { x, y };
};

const HIGH_SAMPLES = buildStubSamples(78, 2.4);
const LOW_SAMPLES = buildStubSamples(62, 1.8);

const STUB_SERIES = [
  {
    channelId: -1,
    sensorName: 'Example Sensor',
    name: 'High Temperature',
    color: HIGH_COLOR,
    units: '°F',
    x: HIGH_SAMPLES.x,
    y: HIGH_SAMPLES.y,
    text: HIGH_SAMPLES.y.map((v) => v.toFixed(2)),
  },
  {
    channelId: -2,
    sensorName: 'Example Sensor',
    name: 'Low Temperature',
    color: LOW_COLOR,
    units: '°F',
    x: LOW_SAMPLES.x,
    y: LOW_SAMPLES.y,
    text: LOW_SAMPLES.y.map((v) => v.toFixed(2)),
  },
];

const STUB_ROWS = [
  {
    channelId: -1,
    sensorName: 'Example Sensor',
    component: 'PCB Assembly',
    sensorChannel: 'High Temperature',
    lastReading: `${HIGH_SAMPLES.y[HIGH_SAMPLES.y.length - 1]} °F`,
    color: HIGH_COLOR,
  },
  {
    channelId: -2,
    sensorName: 'Example Sensor',
    component: 'PCB Assembly',
    sensorChannel: 'Low Temperature',
    lastReading: `${LOW_SAMPLES.y[LOW_SAMPLES.y.length - 1]} °F`,
    color: LOW_COLOR,
  },
];

const SensorDataWidgetDefault = () => {
  const history = useHistory();
  const isPublicView = !!getPublicDashboardToken();

  const handleCreateDataGroup = () => {
    history.push('/s/sensor-data/explore');
  };

  return (
    <div className={`${styles.container} position-relative`}>
      {/* Ghosted preview — graph + table filling the widget as the backdrop. */}
      <div
        aria-hidden="true"
        className="h-100 w-100 d-flex flex-column"
        style={{
          opacity: 0.35,
          filter: 'grayscale(35%)',
          gap: 12,
          minHeight: 0,
          pointerEvents: 'none',
        }}
      >
        <div className={styles.graphCard} style={{ flex: '3 1 0', minHeight: 140 }}>
          <SensorDataGraph series={STUB_SERIES} rangeDays={1} />
        </div>
        <div className={`${styles.tableCard} ${styles.tableWithGraph}`}>
          <div className={styles.tableScroll}>
            <SensorDataTable rows={STUB_ROWS} />
          </div>
        </div>
      </div>

      {/* Floating call-to-action layered over the ghosted preview. */}
      <div
        className="position-absolute d-flex align-items-center justify-content-center"
        style={{
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          padding: '1rem',
        }}
      >
        <div
          className="shadow-sm rounded text-center"
          style={{
            background: 'rgba(255, 255, 255, 0.96)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            padding: '1rem 1.25rem',
            maxWidth: '360px',
            width: '100%',
          }}
        >
          <div className="text-muted small mb-3">
            Graph just the data that matters to you by adding sensor readings to
            a custom Data Group.
          </div>
          {!isPublicView && (
            <HasRolePrivileges targetRoleList={[RaaRoles.MANAGER, RaaRoles.ADMIN]}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreateDataGroup}
              >
                Create Data Group
              </button>
            </HasRolePrivileges>
          )}
        </div>
      </div>
    </div>
  );
};

export default SensorDataWidgetDefault;
